import React, { useState, useEffect } from "react";
import {
  Modal as AntModal,
  Row,
  Col,
  Card,
  Button,
  Typography,
  Space,
  Divider,
  Tag,
  Spin as AntSpin,
  Input,
  Empty,
  Radio,
  Tooltip,
  Badge,
} from "antd";
import {
  FileOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  SearchOutlined,
  AppstoreOutlined,
  SendOutlined,
  SyncOutlined,
  BarChartOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  StarFilled,
} from "@ant-design/icons";
import { IEmailTemplate } from "easy-email-editor";
import { MjmlToJson } from "easy-email-extensions";
import mjml2html from "mjml-browser";

import "@assets/css/TemplateGallery.css";

// Import templates from the organized structure
import { TEMPLATES_ARRAY } from "./preDefinedTemplates";

// Type assertion for antd components to support children prop
const Modal = AntModal as any;
const Spin = AntSpin as any;

const { Title, Text, Paragraph } = Typography;

// Template interface for type safety
interface EmailTemplate {
  id: string;
  name: string;
  category: string;
  purpose?: "campaign" | "sequence" | "pulse" | "transactional";
  description: string;
  features: string[];
  subject?: string;
  subTitle?: string;
  mjmlTemplate: string;
  tags?: string[];
}

// Cast templates array to typed array
const typedTemplates = TEMPLATES_ARRAY as EmailTemplate[];

/**
 * Generate preview HTML for a template with sample data
 */
const generateTemplatePreview = (mjmlTemplate: string): string => {
  try {
    const personalizedMjml = mjmlTemplate
      .replace(/{{first_name}}/g, "John")
      .replace(/{{last_name}}/g, "Doe")
      .replace(/{{company_name}}/g, "Acme Corp")
      .replace(/{{product_name}}/g, "Copilotly")
      .replace(/{{event_name}}/g, "Annual Conference")
      .replace(/{{event_date}}/g, "January 15, 2025")
      .replace(/{{event_time}}/g, "6:00 PM")
      .replace(/{{event_venue}}/g, "Grand Ballroom")
      .replace(/{{discount_amount}}/g, "50%")
      .replace(/{{promo_code}}/g, "SAVE50")
      .replace(/{{total_conversations}}/g, "1,234")
      .replace(/{{total_messages}}/g, "5,678")
      .replace(/{{overall_score}}/g, "87")
      .replace(/{{current_plan}}/g, "Professional")
      .replace(/{{.*?}}/g, "Sample");

    const { html } = mjml2html(personalizedMjml);
    return html;
  } catch (error) {
    return "";
  }
};

/**
 * Helper function to fix social elements in content
 */
const fixSocialElements = (content: any): any => {
  const traverse = (node: any) => {
    if (
      node.type === "social" &&
      node.data &&
      node.data.value &&
      Array.isArray(node.data.value.elements)
    ) {
      node.data.value.elements.forEach((element: any) => {
        if (!element.content || element.content === "undefined") {
          const nameMap: Record<string, string> = {
            Facebook: "Facebook",
            facebook: "Facebook",
            Twitter: "Twitter",
            twitter: "Twitter",
            Instagram: "Instagram",
            instagram: "Instagram",
            LinkedIn: "LinkedIn",
            linkedin: "LinkedIn",
          };
          element.content = nameMap[element.name] || element.name || "Social";
        }
      });
    }
    if (node.children && node.children.length) {
      node.children.forEach(traverse);
    }
  };
  const clonedContent = JSON.parse(JSON.stringify(content));
  traverse(clonedContent);
  return clonedContent;
};

/**
 * Converts MJML content to HTML for preview purposes
 */
const convertMjmlToHtml = (mjmlContent: string): string => {
  try {
    const { html, errors } = mjml2html(mjmlContent);
    if (errors.length) {
      console.error("MJML errors:", errors);
      return `<div style="color: red; padding: 20px;">Error rendering template: ${errors[0].message}</div>`;
    }
    return html;
  } catch (error) {
    console.error("Error converting MJML to HTML:", error);
    return `<div style="color: red; padding: 20px;">Error rendering template</div>`;
  }
};

interface TemplateGalleryProps {
  isVisible: boolean;
  onClose: () => void;
  onSelectTemplate: (template: Partial<IEmailTemplate>) => void;
  filterByPurpose?: "campaign" | "sequence" | "pulse" | "transactional" | null;
}

const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  isVisible,
  onClose,
  onSelectTemplate,
  filterByPurpose = null,
}) => {
  const [purpose, setPurpose] = useState<string>(filterByPurpose || "all");
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [previewHtml, setPreviewHtml] = useState<string>("");

  useEffect(() => {
    if (isVisible) {
      setSelectedTemplate(null);
      setIsPreviewMode(false);
      setSearchQuery("");
      setPreviewHtml("");
      setPurpose(filterByPurpose || "all");
    }
  }, [isVisible, filterByPurpose]);

  useEffect(() => {
    if (selectedTemplate && isPreviewMode) {
      setIsLoading(true);
      setTimeout(() => {
        try {
          const mjmlContent = selectedTemplate.mjmlTemplate;
          const personalizedMjml = mjmlContent
            .replace(/{{first_name}}/g, "John")
            .replace(/{{event_name}}/g, "Annual Cultural Festival")
            .replace(/{{event_date}}/g, "October 15, 2023")
            .replace(/{{event_time}}/g, "6:00 PM")
            .replace(/{{event_venue}}/g, "Grand Ballroom, Marriott Hotel")
            .replace(/{{ticket_type}}/g, "Premium")
            .replace(/{{ticket_quantity}}/g, "2")
            .replace(/{{total_amount}}/g, "$500")
            .replace(/{{registration_id}}/g, "REG-12345")
            .replace(/{{order_reference}}/g, "ORD-67890")
            .replace(/{{transaction_id}}/g, "TXN-123456")
            .replace(/{{transaction_date}}/g, "October 5, 2023")
            .replace(/{{amount_paid}}/g, "$500")
            .replace(/{{payment_method}}/g, "Credit Card")
            .replace(/{{last_four}}/g, "4321")
            .replace(/{{quantity}}/g, "2")
            .replace(/{{unit_price}}/g, "$250")
            .replace(/{{line_total}}/g, "$500")
            .replace(/{{subtotal}}/g, "$500")
            .replace(/{{tax_amount}}/g, "$0")
            .replace(/{{hours}}/g, "23")
            .replace(/{{minutes}}/g, "59")
            .replace(/{{seconds}}/g, "59")
            .replace(/{{days_remaining}}/g, "3")
            .replace(/{{hours_remaining}}/g, "12")
            .replace(/{{minutes_remaining}}/g, "45")
            .replace(/{{seconds_remaining}}/g, "30")
            .replace(/{{discount_amount}}/g, "50%")
            .replace(/{{promo_code}}/g, "SAVE50")
            .replace(/{{total_conversations}}/g, "1,234")
            .replace(/{{total_messages}}/g, "5,678")
            .replace(/{{unique_users}}/g, "892")
            .replace(/{{satisfaction_score}}/g, "94")
            .replace(/{{overall_score}}/g, "87")
            .replace(/{{current_plan}}/g, "Professional")
            .replace(/{{messages_used}}/g, "8,500")
            .replace(/{{messages_limit}}/g, "10,000")
            .replace(/{{messages_percentage}}/g, "85")
            .replace(/{{.*?}}/g, "Sample");

          const html = convertMjmlToHtml(personalizedMjml);
          setPreviewHtml(html);
        } catch (error) {
          console.error("Error generating preview:", error);
          setPreviewHtml(
            `<div style="color: red; padding: 20px;">Error generating preview</div>`
          );
        } finally {
          setIsLoading(false);
        }
      }, 300);
    }
  }, [selectedTemplate, isPreviewMode]);

  // Purpose options with icons
  const purposeOptions = [
    { value: "all", label: "All Templates", icon: <AppstoreOutlined /> },
    { value: "campaign", label: "Campaigns", icon: <SendOutlined /> },
    { value: "sequence", label: "Sequences", icon: <SyncOutlined /> },
    { value: "pulse", label: "Reports", icon: <BarChartOutlined /> },
    { value: "transactional", label: "Transactional", icon: <SafetyOutlined /> },
  ];

  // Purpose colors for tags - matching Copilotly brand
  const purposeColors: Record<string, string> = {
    campaign: "#8B5CF6",
    sequence: "#0EA5E9",
    pulse: "#F59E0B",
    transactional: "#10B981",
  };

  // Category colors - matching Copilotly brand
  const categoryColors: Record<string, string> = {
    marketing: "#EF4444",
    onboarding: "#10B981",
    events: "#8B5CF6",
    transactional: "#F59E0B",
    product: "#0EA5E9",
    pulse: "#F59E0B",
    sequences: "#0EA5E9",
  };

  // Filter templates
  const filteredTemplates = typedTemplates.filter((template: EmailTemplate) => {
    const matchesPurpose = purpose === "all" || template.purpose === purpose;
    const matchesSearch =
      searchQuery === "" ||
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (template.tags &&
        template.tags.some((tag: string) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        ));
    return matchesPurpose && matchesSearch;
  });

  const handleSelectTemplate = (template: any) => {
    setSelectedTemplate(template);
  };

  const handleUseTemplate = () => {
    setIsLoading(true);
    try {
      let content = MjmlToJson(selectedTemplate.mjmlTemplate);
      content = fixSocialElements(content);
      const templateData = {
        subject: selectedTemplate.subject || selectedTemplate.name,
        subTitle:
          selectedTemplate.subTitle ||
          selectedTemplate.description.split(".")[0],
        content,
      };
      onSelectTemplate(templateData);
      setIsLoading(false);
      onClose();
    } catch (error) {
      console.error("Error loading template:", error);
      setIsLoading(false);
    }
  };

  const handleBackToGallery = () => {
    setSelectedTemplate(null);
    setIsPreviewMode(false);
    setPreviewHtml("");
  };

  const getPurposeLabel = (purposeValue: string) => {
    const labels: Record<string, string> = {
      campaign: "Campaign",
      sequence: "Sequence",
      pulse: "Report",
      transactional: "Transactional",
    };
    return labels[purposeValue] || purposeValue;
  };

  const getPurposeIcon = (purposeValue: string) => {
    const icons: Record<string, React.ReactNode> = {
      campaign: <SendOutlined />,
      sequence: <SyncOutlined />,
      pulse: <BarChartOutlined />,
      transactional: <SafetyOutlined />,
    };
    return icons[purposeValue] || <FileOutlined />;
  };

  return (
    <Modal
      className="template-gallery-modal"
      title={
        <div className="template-gallery-header">
          <div className="template-gallery-icon">
            <FileOutlined />
          </div>
          <div>
            <Title level={4} style={{ margin: 0, fontWeight: 700 }}>
              {selectedTemplate
                ? isPreviewMode
                  ? "Template Preview"
                  : "Template Details"
                : "Email Template Gallery"}
            </Title>
            {selectedTemplate ? (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {selectedTemplate.name}
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 13 }}>
                Choose from {typedTemplates.length} professionally designed templates
              </Text>
            )}
          </div>
        </div>
      }
      visible={isVisible}
      onCancel={onClose}
      width={selectedTemplate ? (isPreviewMode ? 850 : 1100) : 1300}
      footer={
        selectedTemplate ? (
          <div className="template-gallery-footer">
            <Button onClick={handleBackToGallery} icon={<ArrowLeftOutlined />}>
              Back to Gallery
            </Button>
            <Space>
              {!isPreviewMode && (
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => setIsPreviewMode(true)}
                >
                  Preview
                </Button>
              )}
              {isPreviewMode && (
                <Button
                  icon={<InfoCircleOutlined />}
                  onClick={() => setIsPreviewMode(false)}
                >
                  Details
                </Button>
              )}
              <Button
                type="primary"
                onClick={handleUseTemplate}
                loading={isLoading}
                icon={<ThunderboltOutlined />}
              >
                Use This Template
              </Button>
            </Space>
          </div>
        ) : null
      }
      style={{ top: 20 }}
    >
      {!selectedTemplate ? (
        <div className="template-gallery-content">
          {/* Filter Bar */}
          <div className="template-filter-bar">
            {/* Purpose Radio Control */}
            {!filterByPurpose && (
              <Radio.Group
                value={purpose}
                onChange={(e) => setPurpose(e.target.value as string)}
                optionType="button"
                buttonStyle="solid"
                className="purpose-segmented"
              >
                {purposeOptions.map((opt) => (
                  <Radio.Button key={opt.value} value={opt.value}>
                    <div className="segmented-option">
                      {opt.icon}
                      <span>{opt.label}</span>
                    </div>
                  </Radio.Button>
                ))}
              </Radio.Group>
            )}

            {/* Search */}
            <div className="filter-controls">
              <Input
                placeholder="Search by name, description, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                allowClear
                style={{ width: 320 }}
              />
            </div>
          </div>

          {/* Results count */}
          <div className="template-results-info">
            <Space>
              <Badge
                count={filteredTemplates.length}
                style={{ backgroundColor: '#1939B7' }}
                showZero
              />
              <Text type="secondary">
                template{filteredTemplates.length !== 1 ? "s" : ""} available
              </Text>
            </Space>
          </div>

          {filteredTemplates.length === 0 ? (
            <div className="empty-templates">
              <Empty
                description={
                  <span>
                    No templates found matching "<strong>{searchQuery}</strong>"
                  </span>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="link" onClick={() => setSearchQuery("")}>
                  Clear search
                </Button>
              </Empty>
            </div>
          ) : (
            <Row gutter={[24, 24]}>
              {filteredTemplates.map((template, index) => (
                <Col key={template.id} xs={24} sm={12} md={8} lg={6}>
                  <Card
                    hoverable
                    className="template-card-new"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    cover={
                      <div className="template-card-cover">
                        <iframe
                          srcDoc={generateTemplatePreview(template.mjmlTemplate)}
                          className="template-card-iframe"
                          title={template.name}
                          scrolling="no"
                        />
                        <div className="template-card-overlay">
                          <Button
                            type="primary"
                            icon={<EyeOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectTemplate(template);
                            }}
                          >
                            View Details
                          </Button>
                        </div>
                        {template.purpose && (
                          <Tooltip title={getPurposeLabel(template.purpose)}>
                            <Tag
                              className="template-purpose-badge"
                              color={purposeColors[template.purpose]}
                              icon={getPurposeIcon(template.purpose)}
                            >
                              {getPurposeLabel(template.purpose)}
                            </Tag>
                          </Tooltip>
                        )}
                      </div>
                    }
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <div className="template-card-body">
                      <Text strong className="template-card-title">
                        {template.name}
                      </Text>
                      <Paragraph
                        type="secondary"
                        ellipsis={{ rows: 2 }}
                        className="template-card-desc"
                      >
                        {template.description}
                      </Paragraph>
                      <div className="template-card-footer">
                        <Tag color={categoryColors[template.category] || "#64748b"}>
                          {template.category}
                        </Tag>
                        <Tooltip title="Quick use this template">
                          <Button
                            type="link"
                            size="small"
                            icon={<ThunderboltOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectTemplate(template);
                              setTimeout(() => handleUseTemplate(), 100);
                            }}
                          >
                            Use
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      ) : isPreviewMode ? (
        <div className="template-preview-container">
          <Spin spinning={isLoading} tip="Generating preview...">
            {previewHtml ? (
              <iframe
                srcDoc={previewHtml}
                className="template-preview-iframe"
                title="Email Template Preview"
              />
            ) : (
              <div className="template-preview-loading">
                <Spin tip="Loading preview..." />
              </div>
            )}
          </Spin>
        </div>
      ) : (
        <div className="template-details-container">
          <Row gutter={[36, 24]}>
            <Col span={10}>
              <div className="template-details-preview">
                <iframe
                  srcDoc={generateTemplatePreview(selectedTemplate.mjmlTemplate)}
                  className="template-details-iframe"
                  title={selectedTemplate.name}
                />
              </div>
            </Col>
            <Col span={14}>
              <div className="template-details-info">
                <Space size={12} style={{ marginBottom: 16 }}>
                  {selectedTemplate.purpose && (
                    <Tag
                      color={purposeColors[selectedTemplate.purpose]}
                      icon={getPurposeIcon(selectedTemplate.purpose)}
                      style={{ fontSize: 12, padding: '4px 12px' }}
                    >
                      {getPurposeLabel(selectedTemplate.purpose)}
                    </Tag>
                  )}
                  <Tag
                    color={categoryColors[selectedTemplate.category]}
                    style={{ fontSize: 12, padding: '4px 12px' }}
                  >
                    {selectedTemplate.category}
                  </Tag>
                </Space>

                <Title level={3} style={{ marginBottom: 12, marginTop: 0 }}>
                  {selectedTemplate.name}
                </Title>

                <Paragraph style={{ fontSize: 15, color: "#64748b", marginBottom: 24 }}>
                  {selectedTemplate.description}
                </Paragraph>

                <Divider style={{ margin: "20px 0" }} />

                <Title level={5} style={{ marginBottom: 16 }}>
                  <StarFilled style={{ color: '#F59E0B', marginRight: 8 }} />
                  Key Features
                </Title>
                <ul className="template-features-list">
                  {selectedTemplate.features.map(
                    (feature: string, i: number) => (
                      <li key={i}>
                        <CheckCircleOutlined className="feature-icon" />
                        <span>{feature}</span>
                      </li>
                    )
                  )}
                </ul>

                {selectedTemplate.tags && selectedTemplate.tags.length > 0 && (
                  <>
                    <Divider style={{ margin: "20px 0" }} />
                    <div className="template-tags">
                      <Text type="secondary" style={{ marginRight: 12, fontWeight: 500 }}>
                        Tags:
                      </Text>
                      {selectedTemplate.tags.map((tag: string, i: number) => (
                        <Tag key={i}>{tag}</Tag>
                      ))}
                    </div>
                  </>
                )}

                <Divider style={{ margin: "24px 0" }} />

                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={handleUseTemplate}
                  loading={isLoading}
                  icon={<ThunderboltOutlined />}
                  style={{ height: 48, fontSize: 16, fontWeight: 600 }}
                >
                  Use This Template
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      )}
    </Modal>
  );
};

export default TemplateGallery;
